<template>
  <div class="reporting-section">
    <div class="section-header">
      <i class="material-icons section-icon">group</i>
      <h2>{{ $t('dash.reporting.section.audience.title') }}</h2>
    </div>

    <div class="metric-row">
      <MetricCard
        :value="stats.page.page_fans || 0"
        :previous-value="prev('page_fans')"
        :label="$t('dash.reporting.followers')"
        icon="group"
      />
      <!-- LinkedIn: organic + paid gains -->
      <template v-if="network === 'linkedin'">
        <MetricCard
          v-if="stats.page.organicFollowerGain != null"
          :value="stats.page.organicFollowerGain || 0"
          :previous-value="prev('organicFollowerGain')"
          :label="$t('dash.reporting.organicFollowerGain')"
          icon="person_add"
        />
        <MetricCard
          v-if="stats.page.paidFollowerGain != null"
          :value="stats.page.paidFollowerGain || 0"
          :previous-value="prev('paidFollowerGain')"
          :label="$t('dash.reporting.paidFollowerGain')"
          icon="paid"
        />
      </template>
      <!-- Facebook / TikTok / other: fan adds + removes -->
      <template v-else>
        <MetricCard
          v-if="sumFanAdds > 0"
          :value="sumFanAdds"
          :previous-value="prev('page_fan_adds_unique') || prev('page_fan_adds')"
          :label="$t('dash.reporting.new_followers')"
          icon="person_add"
        />
        <MetricCard
          v-if="sumFanRemoves > 0"
          :value="sumFanRemoves"
          :previous-value="prev('page_fan_removes_unique') || prev('page_fan_removes')"
          :label="$t('dash.reporting.nb_fans.lost_fans')"
          icon="remove_circle"
        />
      </template>
    </div>

    <template v-if="activeGraphData.length && activeLabels.length">
      <div class="graph-title">{{ $t('dash.reporting.followers') }}</div>
      <div class="graph-wrap">
        <Graph
          :id="`fans-combined-${graphId}`"
          :data="activeGraphData"
          :labels="activeLabels"
          :stacked="false"
          height="220px"
          width="100%"
        />
      </div>
    </template>
  </div>
</template>

<script>
import MetricCard from './panels/MetricCard.vue'
import Graph from './Graph.vue'
import moment from 'moment'

function sortByDate(arr) {
  return arr.slice().sort((a, b) => a.date - b.date)
}
function toLabels(arr) {
  if (!Array.isArray(arr) || !arr.length) return []
  return sortByDate(arr).map(d => moment(d.date * 1000).format('MM/DD/YYYY'))
}
function toDateMap(arr) {
  const map = {}
  if (!Array.isArray(arr)) return map
  arr.forEach(function(d) { map[d.date] = d.total || 0 })
  return map
}

export default {
  name: 'ReportingVisibility',
  components: { MetricCard, Graph },
  props: {
    stats:       { type: Object, required: true },
    graphLabels: { type: Array,  default: () => [] },
    page:        { type: Object, default: null },
  },
  computed: {
    graphId() { return this.page ? this.page.id : 'visibility' },
    network() { return this.page ? (this.page.network || '') : '' },
    sumFanAdds() {
      const arr = this.stats.page.page_fan_adds_unique || this.stats.page.page_fan_adds || []
      if (!Array.isArray(arr)) return arr || 0
      return arr.reduce((s, d) => s + (d.total || 0), 0)
    },
    sumFanRemoves() {
      const arr = this.stats.page.page_fan_removes_unique || this.stats.page.page_fan_removes || []
      if (!Array.isArray(arr)) return Math.abs(arr || 0)
      return arr.reduce((s, d) => s + Math.abs(d.total || 0), 0)
    },
    totalFansArr() {
      return this.stats.page.dailyPageFans_graph || this.stats.page.page_follows || []
    },
    fanAddsArr() {
      const arr = this.stats.page.page_fan_adds_unique || this.stats.page.page_fan_adds || []
      return Array.isArray(arr) ? arr : []
    },
    fanRemovesArr() {
      const arr = this.stats.page.page_fan_removes_unique || this.stats.page.page_fan_removes || []
      return Array.isArray(arr) ? arr : []
    },
    // LinkedIn-specific: organic/paid follows per day
    liOrganicArr() {
      return Array.isArray(this.stats.page.page_daily_follows_unique) ? this.stats.page.page_daily_follows_unique : []
    },
    liPaidArr() {
      return Array.isArray(this.stats.page.page_daily_follows_paid) ? this.stats.page.page_daily_follows_paid : []
    },
    linkedinGraphData() {
      const organic = this.liOrganicArr
      const paid    = this.liPaidArr
      const base = organic.length ? organic : paid
      if (!base.length) return []
      const sortedDates = sortByDate(base).map(function(d) { return d.date })
      const organicMap = toDateMap(organic)
      const paidMap    = toDateMap(paid)
      const datasets = []
      if (organic.length) {
        datasets.push({
          label: this.$t('dash.reporting.organicFollowerGain'),
          data: sortedDates.map(function(d) { return organicMap[d] || 0 }),
          type: 'bar',
          color: '#10B981',
        })
      }
      if (paid.length) {
        datasets.push({
          label: this.$t('dash.reporting.paidFollowerGain'),
          data: sortedDates.map(function(d) { return paidMap[d] || 0 }),
          type: 'bar',
          color: '#4299E1',
        })
      }
      return datasets
    },
    linkedinLabels() {
      const base = this.liOrganicArr.length ? this.liOrganicArr : this.liPaidArr
      return toLabels(base)
    },
    combinedLabels() {
      const base = this.totalFansArr.length ? this.totalFansArr
                 : this.fanAddsArr.length   ? this.fanAddsArr
                 : this.fanRemovesArr
      return toLabels(base)
    },
    combinedGraphData() {
      const base = this.totalFansArr.length ? this.totalFansArr
                 : this.fanAddsArr.length   ? this.fanAddsArr
                 : this.fanRemovesArr
      if (!Array.isArray(base) || !base.length) return []

      const sortedDates = sortByDate(base).map(function(d) { return d.date })
      const fansMap    = toDateMap(this.totalFansArr)
      const addsMap    = toDateMap(this.fanAddsArr)
      const removesMap = toDateMap(this.fanRemovesArr)

      const datasets = []
      if (this.totalFansArr.length) {
        datasets.push({
          label: this.$t('dash.reporting.followers'),
          data: sortedDates.map(function(d) { return fansMap[d] || 0 }),
          type: 'line',
        })
      }
      if (this.fanAddsArr.length) {
        datasets.push({
          label: this.$t('dash.reporting.nb_fans.new_fans'),
          data: sortedDates.map(function(d) { return addsMap[d] || 0 }),
          type: 'bar',
          color: '#10B981',
        })
      }
      if (this.fanRemovesArr.length) {
        datasets.push({
          label: this.$t('dash.reporting.nb_fans.lost_fans'),
          data: sortedDates.map(function(d) { return Math.abs(removesMap[d] || 0) }),
          type: 'bar',
          color: '#EF4444',
        })
      }
      return datasets
    },
    activeGraphData() {
      return this.network === 'linkedin' ? this.linkedinGraphData : this.combinedGraphData
    },
    activeLabels() {
      return this.network === 'linkedin' ? this.linkedinLabels : this.combinedLabels
    },
  },
  methods: {
    prev(field) {
      const p = this.stats.page.previous_period
      return p && p[field] != null ? p[field] : null
    },
  },
}
</script>

<style scoped>
.reporting-section {
  background: #fff;
  border-radius: 5.6px;
  padding: 20px 24px;
  box-shadow: 0 2px 5.6px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}
.section-icon { color: #6B1F4D; font-size: 22px; }
.section-header h2 { font-size: 16px; font-weight: 700; color: #121826; margin: 0; }
.metric-row { display: flex; flex-wrap: wrap; gap: 14px; margin-bottom: 20px; }
.graph-title {
  font-size: 12px;
  font-weight: 600;
  color: #9ba3b7;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 16px 0 6px;
}
.graph-wrap { width: 100%; margin-bottom: 8px; }
</style>
